import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { fmtCurrency, fmtDateTime } from "@/lib/constants";
import { Radar, LogOut, Ticket, ScrollText } from "lucide-react";

export default function CustomerPortal() {
  const { user, logout } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [t, i] = await Promise.all([api.get("/tickets"), api.get("/invoices")]);
        setTickets(t.data); setInvoices(i.data);
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, []);

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/60 bg-card/50 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Radar className="h-4 w-4" />
            </div>
            <div>
              <p className="font-heading text-sm font-bold leading-none">HCIT-CallCRM</p>
              <p className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">Customer portal · {user?.name}</p>
            </div>
          </div>
          <button data-testid="customer-logout" onClick={doLogout} className="rounded-md border border-border/70 px-3 py-2 text-xs">
            <LogOut className="mr-1 inline h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">
        <p className="label-eyebrow text-primary">Overview</p>
        <h1 className="mt-1 font-heading text-3xl font-bold">Your service portal</h1>
        <p className="mt-1 text-sm text-muted-foreground">Track your open service calls, review history and download invoices.</p>

        {err && <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

        <section className="mt-6">
          <div className="mb-3 flex items-center gap-2">
            <Ticket className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Service calls</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {tickets.map((t) => (
              <div key={t.id} className="sharp-card p-4">
                <div className="mb-1 flex items-center justify-between">
                  <span className="font-mono text-[11px] text-muted-foreground">{t.ticket_no}</span>
                  <PriorityBadge priority={t.priority} />
                </div>
                <p className="text-sm font-medium">{t.complaint}</p>
                <div className="mt-2 flex items-center justify-between">
                  <StatusBadge status={t.status} />
                  <p className="text-[11px] text-muted-foreground">{fmtDateTime(t.created_at)}</p>
                </div>
              </div>
            ))}
            {tickets.length === 0 && <p className="text-sm text-muted-foreground">No service calls yet.</p>}
          </div>
        </section>

        <section className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <ScrollText className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Invoices</h2>
          </div>
          <div className="sharp-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-border/60 bg-secondary/30 text-left">
                <tr><th className="px-3 py-2 label-eyebrow">Invoice</th><th className="px-3 py-2 label-eyebrow">Total</th><th className="px-3 py-2 label-eyebrow">Status</th></tr>
              </thead>
              <tbody>
                {invoices.map((i) => (
                  <tr key={i.id} className="border-b border-border/40">
                    <td className="px-3 py-2 font-mono text-xs">{i.invoice_no}</td>
                    <td className="px-3 py-2">{fmtCurrency(i.total)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider
                        ${i.status === "paid" ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}`}>
                        {i.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={3} className="px-3 py-8 text-center text-xs text-muted-foreground">No invoices yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
